import React, { useEffect, useState } from "react";
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import axios from "axios";

import Backdrop from "./../backdrop/Backdrop";
import ImageUploader from "./../imageUploader/ImageUploader";
import "./IdCardForm.css";

const IdCardForm = ({ user, onDetailChange, croppedImage, fullImage, userImage, REACT_APP_BACKEND_URL, getAccessToken, getUser, createSnackbar, bloodGroupList, addressList, imageMaxSize }) => {

  const [uploadButton, setUploadButton] = useState(false);
  const [showInputBox, setShowInputBox] = useState(false);
  const [downloadButton, setDownloadButton] = useState(false);
  const [pronounValue, setPronounValue] = useState("");
  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);

  const [nameValidator, setNameValidator] = useState({ error: false, msg: "" });
  const [empIdValidator, setEmpIdValidator] = useState({ error: false, msg: "" });
  const [pronounValidator, setPronounValidator] = useState({ error: false, msg: "" });
  const [bloodGroupValidator, setBloodGroupValidator] = useState({ error: false, msg: "" });
  const [homeOfficeLocationValidator, setHomeOfficeLocationValidator] = useState({ error: false, msg: "" });
  const [profileImageValidator, setProfileImageValidator] = useState({ error: false, msg: "" });

  useEffect(() => {
    if ((user.croppedImage === "") || (user.homeOfficeLocation === "") || (user.pronouns === "" && pronounValue === "Specify your pronoun") || (user.bloodGroup === "") || (user.empId === "") || (user.name === ""))
      setDownloadButton(true);
    else
      setDownloadButton(false);
  }, [user]);

  const handleNameValidation = () => {
    const username = user.name.toLowerCase().split(' ').filter(s => s != "").map(s => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');
    onDetailChange("name", username);

    if (username === "") {
      setNameValidator({ error: true, msg: "" });
      return false;
    }

    setNameValidator({ error: false, msg: "" });
    return true;
  }

  const handleEmpIdValidation = () => {
    const regId = /^[0-9]+$/;
    if (user.empId === "") {
      setEmpIdValidator({ error: true, msg: "" });
      return false;
    } else if (!regId.test(user.empId) || parseInt(user.empId) <= 0) {
      setEmpIdValidator({ error: true, msg: "Please enter valid employee id" });
      return false;
    }

    setEmpIdValidator({ error: false, msg: "" });
    return true;
  }

  const handlePronounValidation = () => {
    const userPronoun = user.pronouns.replace(/ /g, '').toLowerCase().split('/').map(s => s.charAt(0).toUpperCase() + s.substring(1)).join('/');
    onDetailChange("pronouns", userPronoun);
    const regName = /^[a-zA-Z]+[/]{1}[a-zA-Z]+$/;
    if (userPronoun === "" && pronounValue === "") {
      setPronounValidator({ error: false, msg: "" });
      return true;
    } else if (userPronoun === "" && pronounValue === "Specify your pronoun") {
      setPronounValidator({ error: true, msg: "" });
      return false;
    } else if (!regName.test(userPronoun)) {
      setPronounValidator({ error: true, msg: 'Please enter in format [Ab/Xy]' });
      return false;
    }

    setPronounValidator({ error: false, msg: "" });
    return true;
  }

  const handleBloodGroupValidation = () => {
    if (user.bloodGroup === "") {
      setBloodGroupValidator({ error: true, msg: "" });
      return false;
    }

    setBloodGroupValidator({ error: false, msg: "" });
    return true;
  }

  const handleHomeOfficeLocationValidation = () => {
    if (user.homeOfficeLocation === "") {
      setHomeOfficeLocationValidator({ error: true, msg: "" });
      return false;
    }

    setHomeOfficeLocationValidator({ error: false, msg: "" });
    return true;
  }

  const handleProfileImageValidation = () => {
    if (user.croppedImage === "") {
      setProfileImageValidator({ error: true, msg: "" });
      createSnackbar.error("Please upload a profile image");
      return false;
    }

    setProfileImageValidator({ error: false, msg: "" });
    return true;
  }

  const handleFormData = () => {
    const nameValidation = handleNameValidation();
    const empIdValidation = handleEmpIdValidation();
    const pronounValidation = handlePronounValidation();
    const bloodGroupValidation = handleBloodGroupValidation();
    const homeOfficeLocationValidation = handleHomeOfficeLocationValidation();
    const profileImageValidation = handleProfileImageValidation();

    if (nameValidation && empIdValidation && pronounValidation && bloodGroupValidation && homeOfficeLocationValidation && profileImageValidation) {
      generatePDF();
    }
  }

  const getProfileImageBlob = () => {
    const profileImageByteString = atob(user.croppedImage.split(',')[1]);
    const profileImageMimeType = user.croppedImage.split(',')[0].split(':')[1].split(';')[0];
    const profileImageArrayBuffer = new ArrayBuffer(profileImageByteString.length);

    let ia = new Uint8Array(profileImageArrayBuffer);
    for (let i = 0; i < profileImageByteString.length; i++) {
      ia[i] = profileImageByteString.charCodeAt(i);
    }

    return new Blob([profileImageArrayBuffer], { type: profileImageMimeType });
  }

  const createFormData = async () => {
    const formData = new FormData();

    formData.append('profileImage', await getProfileImageBlob());
    formData.append('bloodGroup', user.bloodGroup);
    formData.append('pronoun', user.pronouns);
    formData.append('officeLocation', user.homeOfficeLocation);

    if (user.role === "admin") {
      formData.append('empId', user.empId);
      formData.append('name', user.name);
    } else {
      const { name } = await getUser();
      formData.append('name', name);
    }

    return formData;
  }

  const downloadPDF = (pdfURL, filename) => {
    const a = document.createElement('a');
    a.href = pdfURL;
    a.download = filename;
    a.click();
    a.remove();
  }

  const generatePDF = async () => {
    setDownloadButtonLoading(true);
    try {
      const formData = await createFormData();
      createSnackbar.info("Please wait while your identity card is being downloaded");
      const { data, headers } = await axios.post(`${REACT_APP_BACKEND_URL}/idCard`, formData, {
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${await getAccessToken()}`,
          'content-type': 'multipart/form-data'
        }
      });

      const pdfBlob = new Blob([data], { type: headers["content-type"] });
      const filename = `${user.empId}.pdf`;
      const pdfURL = URL.createObjectURL(pdfBlob);

      downloadPDF(pdfURL, filename);
      createSnackbar.success("Identity card downloaded");
    } catch (error) {
      if (error.response) {
        const { message } = JSON.parse(await error.response.data.text());
        if (error.response.status === 401) {
          createSnackbar.reloadError("Unauthorized request, please reload the page");
        } else if (error.response.status === 400) {
          createSnackbar.error(message);
        } else {
          createSnackbar.error("Internal server error, please try again after sometime");
        }
      } else if (error.request) {
        createSnackbar.error("Server down, please try again after sometime");
      } else {
        createSnackbar.error("Something went wrong, please try again after sometime");
      }
    } finally {
      setDownloadButtonLoading(false);
    }
  }

  const handleUploadButton = () => {
    setUploadButton(!uploadButton);
  }

  const handleOfficeLocation = () => {
    return addressList.map((office) => {
      return <MenuItem key={office.id} value={office.name}>{office.name}</MenuItem>;
    });
  }

  return (
    <div className="id-form">
      <Typography variant="subtitle1" component="p" fontWeight="fontWeightBold">
        Your Details
      </Typography>
      <TextField
        required
        fullWidth
        variant="outlined"
        margin="normal"
        label="Full name"
        size="small"
        value={user.name}
        error={nameValidator.error}
        helperText={nameValidator.msg}
        disabled={user.role !== "admin"}
        onChange={(e) => onDetailChange("name", e.target.value)}
        onBlur={() => handleNameValidation()}
      />

      <TextField
        required
        fullWidth
        variant="outlined"
        margin="normal"
        label="Employee ID"
        size="small"
        value={user.empId}
        error={empIdValidator.error}
        helperText={empIdValidator.msg}
        disabled={user.role !== "admin"}
        onChange={(e) => onDetailChange("empId", e.target.value)}
        onBlur={() => handleEmpIdValidation()}
      />

      <TextField
        select
        fullWidth
        variant="outlined"
        margin="normal"
        label="Pronoun"
        size="small"
        value={pronounValue}
        error={!showInputBox && pronounValidator.error}
        helperText={!showInputBox && pronounValidator.msg}
        onChange={(e) => {
          setPronounValue(e.target.value);
          if (e.target.value === "Specify your pronoun") {
            setShowInputBox(true);
            onDetailChange("pronouns", "");
            setPronounValidator({ error: false, msg: "" })
            return;
          } else {
            setShowInputBox(false);
          }
          onDetailChange("pronouns", e.target.value);
        }}
        onBlur={() => { !showInputBox && handlePronounValidation() }}
      >
        <MenuItem value="He/Him">
          He/Him
        </MenuItem>
        <MenuItem value="She/Her">
          She/Her
        </MenuItem>
        <MenuItem value="They/Them">
          They/Them
        </MenuItem>
        <MenuItem value="Specify your pronoun">
          Specify your pronoun
        </MenuItem>
        <MenuItem value="">
          Do not want to specify
        </MenuItem>
      </TextField>

      {
        showInputBox &&
        <TextField
          required
          fullWidth
          variant="outlined"
          margin="normal"
          label="Fill preferred pronoun"
          size="small"
          value={user.pronouns}
          error={showInputBox && pronounValidator.error}
          helperText={showInputBox && pronounValidator.msg}
          onChange={(e) => onDetailChange("pronouns", e.target.value)}
          onBlur={() => { showInputBox && handlePronounValidation() }}
        />
      }

      <TextField
        required
        select
        fullWidth
        variant="outlined"
        margin="normal"
        label="Blood group"
        size="small"
        value={user.bloodGroup}
        error={bloodGroupValidator.error}
        helperText={bloodGroupValidator.msg}
        onChange={(e) => onDetailChange("bloodGroup", e.target.value)}
        onBlur={() => handleBloodGroupValidation()}
      >
        {
          bloodGroupList.map(bloodGroup =>
            <MenuItem value={bloodGroup} key={bloodGroup}>
              {bloodGroup}
            </MenuItem>
          )
        }
      </TextField>

      <TextField
        required
        select
        fullWidth
        variant="outlined"
        margin="normal"
        label="Home office location"
        size="small"
        value={user.homeOfficeLocation}
        error={homeOfficeLocationValidator.error}
        helperText={homeOfficeLocationValidator.msg}
        onChange={(e) => onDetailChange("homeOfficeLocation", e.target.value)}
        onBlur={() => handleHomeOfficeLocationValidation()}
      >
        {handleOfficeLocation()}
      </TextField>

      <Button
        variant="contained"
        color="primary"
        size="medium"
        sx={{ mt: 2, mb: 1, width: "100%" }}
        title="Please click to upload the image*"
        onMouseDown={handleUploadButton}
      >
        Upload image
      </Button>

      <LoadingButton
        variant="contained"
        color="success"
        size="medium"
        sx={{ mt: 2, mb: 1 }}
        title="Please click to download pdf"
        onMouseDown={handleFormData}
        disabled={downloadButton}
        loading={downloadButtonLoading}
      >
        Download
      </LoadingButton>

      <Backdrop open={uploadButton}>
        <ImageUploader
          close={handleUploadButton}
          croppedImage={croppedImage}
          fullImage={fullImage}
          src={userImage}
          createSnackbar={createSnackbar}
          imageMaxSize={imageMaxSize}
        />
      </Backdrop>
    </div>
  )
}

export default IdCardForm;
